/**
 * useDeletePreInvoice - Hook para eliminar prefacturas
 * 
 * Proporciona funcionalidad para eliminar una prefactura con manejo de errores
 * y notificaciones toast.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { PreInvoicesApiAdapter } from '../adapters/PreInvoicesApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

export const useDeletePreInvoice = () => {
  const { t } = useTranslation('preInvoicesModule');
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const deletePreInvoice = async (
    preInvoiceId: number,
    onSuccess?: () => void
  ) => {
    setIsDeleting(true);

    try {
      console.log('🔍 useDeletePreInvoice - Eliminando prefactura:', { preInvoiceId });

      const httpClient = createHttpClient(apiClientWH);
      const adapter = new PreInvoicesApiAdapter(httpClient);
      const response = await adapter.deletePreInvoice(preInvoiceId);

      console.log('🔍 useDeletePreInvoice - Respuesta:', response);

      if (response.success) {
        // 🎯 Construir mensaje de éxito con información adicional si está disponible
        let description = response.message || t('toast.delete_success.description', 'La prefactura se ha eliminado exitosamente');
        
        if (response.shipments_released !== undefined || response.storages_released !== undefined) {
          const parts: string[] = [];
          if (response.shipments_released !== undefined && response.shipments_released > 0) {
            parts.push(t('toast.delete_success.shipments_released', {
              count: response.shipments_released,
              defaultValue: `${response.shipments_released} envío(s) liberado(s)`
            }));
          }
          if (response.storages_released !== undefined && response.storages_released > 0) {
            parts.push(t('toast.delete_success.storages_released', {
              count: response.storages_released,
              defaultValue: `${response.storages_released} almacenamiento(s) liberado(s)`
            }));
          }
          if (parts.length > 0) {
            description += `. ${parts.join(', ')}.`;
          }
        }

        // 🎯 Mostrar toast de éxito con mensaje de la API
        toast({
          title: t('toast.delete_success.title', 'Prefactura eliminada'),
          description,
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch, etc.)
        onSuccess?.();
      } else {
        // 🎯 Mostrar error devuelto por la API
        toast({
          title: t('toast.delete_error.title', 'Error al eliminar'),
          description: response.message || t('toast.delete_error.unexpected', 'Error inesperado al eliminar la prefactura'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 useDeletePreInvoice - Error:', error);

      // 🎯 Extraer mensaje de error
      let errorMessage = t('toast.delete_error.unexpected', 'Error inesperado al eliminar la prefactura. Inténtalo de nuevo.');
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || errorMessage;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // 🎯 Mostrar error inesperado
      toast({
        title: t('toast.delete_error.title', 'Error al eliminar'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deletePreInvoice,
    isDeleting,
  };
};

